<template>
  <div
    ref="vzSidebar"
    v-on-click-outside="onClickOutside"
    v-resize="calcHandlerHeight"
    class="vz-sidebar"
    :class="sidebarClass"
    :style="sidebarStyle"
    @scroll="onScroll"
  >
    <div
      ref="sidebarHandler"
      class="vz-sidebar-handler"
      @touchstart.prevent.stop="startSwipe"
      @touchend.prevent.stop="stopSwipe"
      @touchmove.prevent.stop="onTouchmove"
      @mousedown.prevent.stop="onMouseDown"
    >
      <transition name="fade">
        <slot v-if="!visible" name="title">
          <span :class="titleClass" v-text="handlerText" />
        </slot>
      </transition>
    </div>

    <div ref="sidebarBody" class="vz-sidebar-body">
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import { PropType } from 'vue'
import {Timeout, VzClass} from '~/types/types'
import $breakpoints from '~/composables/breakpoints';
import { useElementBounding } from '@vueuse/core'

const props = defineProps({
  placement: {
    type: String as PropType<'top'|'right'|'left'|'bottom'>,
    default: 'bottom',
  },
  handlerText: {
    type: String,
    default: 'handler',
  },
  titleClass: {
    type: String,
    default: '' ,
  },
})

const vzSidebar = ref<HTMLElement>()
const sidebarBody = ref<HTMLElement>()
const sidebarHandler = ref<HTMLElement>()
const { height: handlerHeight } = useElementBounding(sidebarHandler)
const { height } = useElementBounding(sidebarBody)
const elBounding = useElementBounding(vzSidebar)
// const useSwiper = usePointerSwipe(sidebarHandler) можно перейти на useSwipe


/** sidebar отркрыт (при полном октрытии и при половине свайпа) */
const visible = ref(false)
const initialVisibility = ref<boolean>()
const hidden = ref(false)
/** идёт свайп */
const touched = ref(false)
/** свайп закончился и прошла анимация */
const stable = ref(true)
const stopMouseEvents = ref(false)
const swipePosition = ref(0)
const startSwipePosition = ref(0)
/** Быстрый свайп */
const isFast = ref(false)
let timer: Timeout = null
let stableTimer: Timeout = null

const sidebarClass = computed((): VzClass => {
  return {
    [`vz-sidebar-${props.placement}`]: props.placement,
    'vz-sidebar-show': visible.value,
    'vz-sidebar-hide': !visible.value,
    'vz-sidebar-hidden': hidden.value,
    'vz-sidebar-touched': touched.value,
    'vz-sidebar-stable': stable.value
  }
})

onMounted((): void => {
  calcHandlerHeight()
})

onBeforeUnmount((): void => {
  window.removeEventListener('mousemove', onMouseMove)
  window.removeEventListener('mouseup', stopSwipe)
})

const sidebarStyle = computed(() => {
  const distance = swipePosition.value - startSwipePosition.value
  const topPosition = Math.max(-window?.innerHeight + elBounding.height.value + 110, 0)
  let bottom = initialVisibility.value
    ? topPosition
    : elBounding.height.value - (visible.value ? handlerHeight.value * 2 : handlerHeight.value)

  if (!touched.value || Math.abs(distance) < 10) {
    bottom = visible.value
      ? topPosition
      : elBounding.height.value - handlerHeight.value
  } else {
    bottom += distance
  }
  if (bottom < 0) {
    bottom = 0
  }
  return {
    bottom: `${-bottom}px`,
    transition: initialVisibility.value === undefined ? 'unset' : undefined
  }
})

const onScroll = (): void => {
  // При скроле сайдбара вызываем событие scroll на window, чтобы отработала директива v-scroll у дочерних элементов
  window.dispatchEvent(new CustomEvent('scroll'))
}

const calcHandlerHeight = (): void => {
  hidden.value = $breakpoints.keyboardVisibility && !visible.value
}

const touch = (position: number): void => {
  if (touched.value) {
    return
  }
  window.addEventListener('mousemove', onMouseMove)
  window.addEventListener('mouseup', stopSwipe)
  initialVisibility.value = visible.value
  touched.value = true
  swipePosition.value = position
  startSwipePosition.value = position
  isFast.value = true
  stable.value = false
  if (stableTimer) {
    clearTimeout(stableTimer)
  }
  timer = setTimeout(function() {
    isFast.value = false
  }, 200);
}

const unTouch = (): void => {
  touched.value = false
  stableTimer = setTimeout(function() {
    stable.value = true
  }, 300);
  window.removeEventListener('mousemove', onMouseMove)
  window.removeEventListener('mouseup', stopSwipe)
}

const onMouseMove = (event: MouseEvent): void => {
  move(event.y, event)
}

const onTouchmove = (e: TouchEvent): void => {
  move(e.touches[0].clientY, e)
}

const onMouseDown = (event: MouseEvent): void => {
  if (stopMouseEvents.value) {
    return
  }
  touch(event.clientY)
}

const startSwipe = ({ touches }: TouchEvent): void => {
  // Нужно чтобы блокировать события клика мышки, иначе события с тачем конфликтуют
  stopMouseEvents.value = true
  touch(touches[0].clientY)
}

const onClickOutside = (): void => {
  visible.value = false
}

const stopSwipe = (): void => {
  if (!touched.value) {
    return
  }
  if (startSwipePosition.value - swipePosition.value === 0) {
    visible.value = !visible.value
  } else if (isFast.value) {
    const bottomOffset = window.innerHeight - swipePosition.value
    if (initialVisibility.value && (height.value / 1.5 > bottomOffset)) {
      visible.value = false
    } else if (!initialVisibility.value && (height.value / 6 < bottomOffset)) {
      visible.value = true
    }
  }
  unTouch()
}

const move = (newPosition: number, e: Event): void => {
  if (!touched.value) {
    return
  }
  e.preventDefault()
  swipePosition.value = newPosition// Math.max(newPosition, window.innerHeight - height.value - handlerHeight.value)
  const bottomOffset = window.innerHeight - swipePosition.value
  const bottomOfMiddle = (swipePosition.value > bottomOffset) && (height.value / 2 > bottomOffset)
  // Скрыть title при скролинге
  if (visible.value && bottomOfMiddle) {
    visible.value = false
  } else if (!visible.value && !bottomOfMiddle) {
    visible.value = true
  }
}

defineExpose({
  visible
})
</script>